import Search from "@mui/icons-material/Search";
import { Box, CircularProgress, IconButton, Stack, TextField, Typography } from "@mui/material";
import { useCallback, useRef, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { useToken } from "../../server/auth/use-token";
import { authHeader } from "../../utils/auth-header";
import {
  AiImageStyleConfig,
  AiImageStyleToggle,
  type AiImageStyle,
} from "../brand-kit/components/ai-image-style-toggle";
import type { BrandInfoSelected } from "../brand-kit/types";
import { selfDriving } from "../self-driving/self-driving";

const NUM_IMAGES = 4;

type ImageResult = {
  url: string | null;
  loading: boolean;
  error: boolean;
};

export const AiTab: FC<{
  onImageClick?: (imageUrl: string) => void;
  selectedImage: string | null;
  onSelectionChange?: (hasSelection: boolean) => void;
  hidden?: boolean;
  branding?: BrandInfoSelected;
}> = ({ onImageClick, selectedImage, onSelectionChange, hidden = false, branding }) => {
  const { t } = useTranslation();
  const getToken = useToken();
  const [query, setQuery] = useState("");
  const [imageResults, setImageResults] = useState<ImageResult[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState(branding?.ai_image_style ?? "illustration");
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleImageClick = (url: string): void => {
    onImageClick?.(url);
    onSelectionChange?.(true);
  };

  const handleSearch = useCallback(async () => {
    const trimmedQuery = query.trim();

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (!trimmedQuery) {
      setImageResults([]);
      setHasSearched(false);
      return;
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const initialResults: ImageResult[] = Array.from({ length: NUM_IMAGES }, () => ({
      url: null,
      loading: true,
      error: false,
    }));
    setImageResults(initialResults);
    setHasSearched(true);

    const token = await getToken();

    const requests = Array.from({ length: NUM_IMAGES }, (_, index) =>
      (async () => {
        try {
          const { data, error } = await selfDriving.GET("/photos/ai", {
            params: {
              query: { query: trimmedQuery, style: selectedStyle },
            },
            ...authHeader(token),
            signal: abortController.signal,
          });

          if (abortController.signal.aborted) return;

          if (error || !data?.url) {
            setImageResults((prev) => {
              const updated = [...prev];
              updated[index] = { url: null, loading: false, error: true };
              return updated;
            });
            return;
          }

          setImageResults((prev) => {
            const updated = [...prev];
            updated[index] = { url: data.url, loading: false, error: false };
            return updated;
          });
        } catch (err) {
          if (err instanceof Error && err.name === "AbortError") {
            return;
          }
          console.error(`AI image generation failed for index ${index}:`, err);
          setImageResults((prev) => {
            const updated = [...prev];
            updated[index] = { url: null, loading: false, error: true };
            return updated;
          });
        }
      })(),
    );

    await Promise.allSettled(requests);
  }, [getToken, query, selectedStyle]);

  const isLoading = imageResults.some((r) => r.loading);
  const hasResults = imageResults.some((r) => r.url !== null);
  const allFinishedWithNoResults =
    hasSearched && imageResults.length > 0 && !isLoading && !hasResults;

  return (
    <Box
      sx={{
        flex: 1,
        overflowY: "auto",
        px: 3,
        py: 2.5,
        backgroundColor: "background.default",
        display: hidden ? "none" : "block",
      }}
    >
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        {t("Unique images. Can take more time to generate.")}
      </Typography>

      <Stack direction="row" sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mr: 2 }}>
          {t("Choose an image style")}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {AiImageStyleConfig[selectedStyle as AiImageStyle]?.name}
        </Typography>
      </Stack>
      <Box sx={{ mb: 2 }}>
        <AiImageStyleToggle aiImageStyle={selectedStyle} onChange={(v) => setSelectedStyle(v)} />
      </Box>

      <TextField
        fullWidth
        placeholder={t("Describe the image you want")}
        size="small"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            void handleSearch();
          }
        }}
        sx={{ mb: 2, backgroundColor: "white" }}
        slotProps={{
          input: {
            endAdornment: (
              <IconButton
                data-analytics-id="ai-tab-search-button"
                onClick={() => void handleSearch()}
                size="small"
                disabled={!query.trim()}
                sx={{ color: "text.secondary" }}
              >
                <Search />
              </IconButton>
            ),
          },
        }}
      />

      {hasResults && !isLoading && (
        <Typography variant="overline" color="text.secondary">
          {t("Generated images")}
        </Typography>
      )}
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        }}
      >
        {allFinishedWithNoResults ? (
          <Box
            sx={{
              gridColumn: "1 / -1",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 4,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t("No results")}
            </Typography>
          </Box>
        ) : (
          imageResults.map((result, index) => (
            <Box
              key={index}
              onClick={() => result.url && handleImageClick(result.url)}
              sx={(theme) => ({
                borderRadius: 2,
                overflow: "hidden",
                backgroundColor: "background.default",
                border:
                  selectedImage === result.url
                    ? `3px solid ${theme.palette.primary.main}`
                    : `1px solid ${theme.palette.divider}`,
                aspectRatio: "3 / 4",
                position: "relative",
                cursor: result.url ? "pointer" : "default",
                "&:hover": {
                  borderColor: result.url ? theme.palette.primary.light : undefined,
                },
              })}
            >
              {result.loading ? (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              ) : result.error || !result.url ? (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="caption" color="text.disabled">
                    {t("Failed")}
                  </Typography>
                </Box>
              ) : (
                <Box
                  component="img"
                  src={result.url}
                  alt=""
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              )}
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};
